import type { ReactElement, RefObject } from 'react'
import type { TFunction } from 'i18next'
import {
  CheckSquare,
  ChevronDown,
  Filter,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import type { ListEndpointsQuery } from '../../../../types/case-container'
import type { ActiveFilter, FilterDefinition } from '../case-infrastructure-tab.helpers'

interface FilterDropdownProps {
  filterDropdownRef: RefObject<HTMLDivElement>
  showFilterDropdown: boolean
  setShowFilterDropdown: (v: boolean | ((prev: boolean) => boolean)) => void
  selectedFilterField: FilterDefinition | null
  setSelectedFilterField: (v: FilterDefinition | null) => void
  availableFilters: FilterDefinition[]
  addFilter: (def: FilterDefinition, value: string) => void
}

export function FilterDropdown(props: FilterDropdownProps): ReactElement {
  const {
    filterDropdownRef,
    showFilterDropdown,
    setShowFilterDropdown,
    selectedFilterField,
    setSelectedFilterField,
    availableFilters,
    addFilter,
  } = props
  return (
    <div className="relative" ref={filterDropdownRef}>
      <button
        onClick={() => {
          setShowFilterDropdown((prev) => !prev)
          setSelectedFilterField(null)
        }}
        disabled={availableFilters.length === 0}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <Filter className="h-4 w-4" />
        <span className="hidden sm:inline">Add Filter</span>
        <ChevronDown className="h-3 w-3" />
      </button>
      {showFilterDropdown && (
        <FilterDropdownPanel
          selectedFilterField={selectedFilterField}
          setSelectedFilterField={setSelectedFilterField}
          availableFilters={availableFilters}
          addFilter={addFilter}
        />
      )}
    </div>
  )
}

interface FilterDropdownPanelProps {
  selectedFilterField: FilterDefinition | null
  setSelectedFilterField: (v: FilterDefinition | null) => void
  availableFilters: FilterDefinition[]
  addFilter: (def: FilterDefinition, value: string) => void
}

function FilterDropdownPanel(props: FilterDropdownPanelProps): ReactElement {
  const { selectedFilterField, setSelectedFilterField, availableFilters, addFilter } = props
  return (
    <div className="absolute top-full left-0 z-50 mt-1 w-64 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
      {selectedFilterField ? (
        <FilterValuePicker
          field={selectedFilterField}
          onBack={() => {
            setSelectedFilterField(null)
          }}
          onPick={(value) => {
            addFilter(selectedFilterField, value)
          }}
        />
      ) : (
        <FilterFieldList availableFilters={availableFilters} onPick={setSelectedFilterField} />
      )}
    </div>
  )
}

interface FilterFieldListProps {
  availableFilters: FilterDefinition[]
  onPick: (def: FilterDefinition) => void
}

function FilterFieldList({ availableFilters, onPick }: FilterFieldListProps): ReactElement {
  return (
    <div className="p-2">
      <p className="px-2 py-1 text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Select Filter
      </p>
      {availableFilters.map((filterDef) => (
        <button
          key={filterDef.id}
          onClick={() => {
            onPick(filterDef)
          }}
          className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          {filterDef.label}
        </button>
      ))}
    </div>
  )
}

interface FilterValuePickerProps {
  field: FilterDefinition
  onBack: () => void
  onPick: (value: string) => void
}

function FilterValuePicker({ field, onBack, onPick }: FilterValuePickerProps): ReactElement {
  return (
    <div className="p-2">
      <button
        onClick={onBack}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
      >
        <ChevronDown className="h-3 w-3 rotate-90" />
        Back
      </button>
      <p className="px-2 py-1 text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {field.label}
      </p>
      <div className="max-h-48 overflow-y-auto">
        {field.options.map((option) => (
          <button
            key={option.value}
            onClick={() => {
              onPick(option.value)
            }}
            className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

interface SearchBoxProps {
  searchInput: string
  setSearchInput: (v: string) => void
  handleSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  clearSearch: () => void
  t: TFunction<'cases'>
}

export function SearchBox({
  searchInput,
  setSearchInput,
  handleSearchKeyDown,
  clearSearch,
  t,
}: SearchBoxProps): ReactElement {
  return (
    <div className="relative flex-1">
      <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={searchInput}
        onChange={(event) => {
          setSearchInput(event.target.value)
        }}
        onKeyDown={handleSearchKeyDown}
        placeholder={t('infrastructure.placeholders.searchEndpoints')}
        className="w-full rounded-lg border border-slate-300 bg-white py-2 pr-10 pl-10 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
      />
      {searchInput && (
        <button
          onClick={clearSearch}
          className="absolute top-1/2 right-3 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          title="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

interface SearchButtonProps {
  loading: boolean
  onClick: () => void
}

export function SearchButton({ loading, onClick }: SearchButtonProps): ReactElement {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
    >
      {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
      <span className="hidden sm:inline">Search</span>
    </button>
  )
}

interface ActiveFiltersRowProps {
  activeFilters: ActiveFilter[]
  removeFilter: (id: keyof ListEndpointsQuery) => void
  activeSearch: string
  clearSearch: () => void
  clearAllFilters: () => void
}

export function ActiveFiltersRow(props: ActiveFiltersRowProps): ReactElement {
  const { activeFilters, removeFilter, activeSearch, clearSearch, clearAllFilters } = props
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SlidersHorizontal className="h-4 w-4 text-slate-400" />
      <span className="text-sm text-slate-500 dark:text-slate-400">Filters:</span>
      {activeFilters.map((filter) => (
        <FilterChip
          key={filter.fieldId}
          filter={filter}
          onRemove={() => {
            removeFilter(filter.fieldId)
          }}
        />
      ))}
      {activeSearch && <SearchChip activeSearch={activeSearch} onClear={clearSearch} />}
      <button
        onClick={clearAllFilters}
        className="text-xs text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
      >
        Clear all
      </button>
    </div>
  )
}

function FilterChip({
  filter,
  onRemove,
}: {
  filter: ActiveFilter
  onRemove: () => void
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-sm text-blue-700 dark:bg-blue-950/50 dark:text-blue-400">
      <span className="font-medium">{filter.fieldLabel}:</span>
      <span>{filter.valueLabel}</span>
      <button
        onClick={onRemove}
        className="ml-1 rounded-full p-0.5 hover:bg-blue-200 dark:hover:bg-blue-900"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

function SearchChip({
  activeSearch,
  onClear,
}: {
  activeSearch: string
  onClear: () => void
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-lime-100 px-2 py-1 text-sm text-[#5c6e94] dark:bg-lime-950/50 dark:text-lime-400">
      <span className="font-medium">Search:</span>
      <span>&quot;{activeSearch}&quot;</span>
      <button
        onClick={onClear}
        className="ml-1 rounded-full p-0.5 hover:bg-lime-200 dark:hover:bg-[#8a9bba]/15"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

interface SelectionBarProps {
  selectionCount: number
  clearAll: () => unknown
}

export function SelectionBar({ selectionCount, clearAll }: SelectionBarProps): ReactElement {
  return (
    <div className="flex items-center justify-between rounded-lg border border-[#4e5e82]/15 bg-[#4e5e82]/5 px-4 py-2 dark:border-[#4e5e82] dark:bg-[#8a9bba]/10">
      <div className="flex items-center gap-3">
        <CheckSquare className="h-4 w-4 text-lime-600 dark:text-lime-400" />
        <span className="text-sm font-medium text-lime-700 dark:text-lime-300">
          {selectionCount} endpoint{selectionCount === 1 ? '' : 's'} selected
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            void clearAll()
          }}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-lime-600 transition-colors hover:bg-lime-100 dark:text-lime-400 dark:hover:bg-[#8a9bba]/15"
        >
          <X className="h-3 w-3" />
          Clear selection
        </button>
      </div>
    </div>
  )
}
